import { useState, useRef, useLayoutEffect } from 'react'
import Icon from './Icon'
import styles from './CaseMap.module.css'

// After this many symptom/problem tiles, the rest collapse behind a "+N more"
// tile that expands them in place (per the design doc's ellipsis-tile rule).
const MAX_TILES = 3

// Set equality by membership — guards the truncation setState so re-measuring an
// unchanged layout doesn't spin a render.
function sameSet(a, b) {
  if (a.size !== b.size) return false
  for (const k of a) if (!b.has(k)) return false
  return true
}

/**
 * CaseMap — the Symptom → Problem → Root Cause funnel. Shared by the Cases list card
 * (CaseCard) and the case detail page's "Relationship Map". Columns share a common
 * horizontal centerline (their tile stacks are vertically centered within an equal-
 * height row), and every source tile emits one converging line into the next stage —
 * a left-to-right funnel drawn as a single measured SVG overlay: tile counts (1–4) and
 * the "+N more" expansion change the geometry at runtime, so exact tile centers are
 * read from the DOM rather than assumed.
 *
 * Props:
 *   symptoms       {Array<{name, evidence?, description?}>}
 *   problems       {Array<{name, evidence?, description?}>}
 *   resolutionCode {string}  — the Root Cause tile label (single)
 *   stopPropagation {boolean} — when true, the "+N more" expander stops click bubbling
 *                               (needed inside the clickable CaseCard; harmless elsewhere)
 */
export default function CaseMap({ symptoms = [], problems = [], resolutionCode, stopPropagation = false }) {
  const [showAllSymptoms, setShowAllSymptoms] = useState(false)
  const [showAllProblems, setShowAllProblems] = useState(false)

  const mapRef = useRef(null)
  const [conn, setConn] = useState({ w: 0, h: 0, lines: [], chevrons: [] })
  // Keys (`${variant}-${index}`) of tiles whose text is clipped — drives the hover rule.
  const [truncated, setTruncated] = useState(() => new Set())

  useLayoutEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Recompute the funnel from the live layout. The convergence point for each
    // stage is the vertical center of the *next* column's tile block; each source
    // tile fans a cubic bezier (horizontal tangents) into it. GAP keeps a small
    // visible breath between the line ends and the tiles; a chevron caps the
    // convergence point.
    const GAP = 6
    const measure = () => {
      const mapRect = map.getBoundingClientRect()
      if (!mapRect.width) return
      const cols = Array.from(map.querySelectorAll('[data-col]'))
      const lines = []
      const chevrons = []
      for (let i = 0; i < cols.length - 1; i++) {
        const source = cols[i]
        const target = cols[i + 1]
        // Don't funnel into an empty stage: if the next column has no real tiles
        // (no problems, or no resolution code), draw no lines or chevron into it.
        if (target.querySelectorAll('[data-tile]').length === 0) continue
        const variant = source.getAttribute('data-col')
        const tRect = target.getBoundingClientRect()
        const x2 = tRect.left - mapRect.left - GAP
        const y2 = tRect.top - mapRect.top + tRect.height / 2
        let any = false
        source.querySelectorAll('[data-tile]').forEach(tile => {
          const r = tile.getBoundingClientRect()
          const x1 = r.right - mapRect.left + GAP
          const y1 = r.top - mapRect.top + r.height / 2
          const mx = (x1 + x2) / 2
          lines.push({ variant, d: `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}` })
          any = true
        })
        // One chevron per stage, at the shared convergence point, pointing at the
        // next column.
        if (any) chevrons.push({ variant, x: x2, y: y2 })
      }

      // Clip detection for the hover rule: a tile whose text overflows its box (ellipsis)
      // is "truncated" and earns its full text in the tooltip. +1 absorbs sub-pixel noise.
      const trunc = new Set()
      map.querySelectorAll('[data-tile][data-tilekey]').forEach(tile => {
        if (tile.scrollWidth > tile.clientWidth + 1) trunc.add(tile.dataset.tilekey)
      })
      setTruncated(prev => (sameSet(prev, trunc) ? prev : trunc))

      setConn({ w: mapRect.width, h: mapRect.height, lines, chevrons })
    }

    // ResizeObserver delivers an initial measurement on observe() and re-fires on
    // any layout change (expansion, responsive resize) — so the setState stays out
    // of the effect body and the lines always track the current DOM.
    const ro = new ResizeObserver(measure)
    ro.observe(map)
    return () => ro.disconnect()
  }, [symptoms, problems, resolutionCode, showAllSymptoms, showAllProblems])

  return (
    <div className={styles.map} ref={mapRef}>
      {conn.w > 0 && (
        <svg
          className={styles.connectors}
          width={conn.w}
          height={conn.h}
          viewBox={`0 0 ${conn.w} ${conn.h}`}
          aria-hidden="true"
        >
          {conn.lines.map((l, i) => (
            <path key={i} d={l.d} fill="none" className={styles[`line_${l.variant}`]} />
          ))}
          {conn.chevrons.map((c, i) => (
            <polyline
              key={i}
              points={`${c.x - 5},${c.y - 4} ${c.x},${c.y} ${c.x - 5},${c.y + 4}`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`${styles[`line_${c.variant}`]} ${styles.chevronCap}`}
            />
          ))}
        </svg>
      )}

      <MapColumn
        variant="symptom"
        label={symptoms.length === 1 ? 'Symptom' : 'Symptoms'}
        icon={<WarningIcon />}
        items={symptoms}
        expanded={showAllSymptoms}
        onExpand={() => setShowAllSymptoms(true)}
        stopPropagation={stopPropagation}
        truncated={truncated}
      />
      <GapChevron />
      <MapColumn
        variant="problem"
        label={problems.length === 1 ? 'Problem' : 'Problems'}
        icon={<SearchIcon />}
        items={problems}
        expanded={showAllProblems}
        onExpand={() => setShowAllProblems(true)}
        stopPropagation={stopPropagation}
        truncated={truncated}
      />
      <GapChevron />
      <MapColumn
        variant="resolution"
        label="Root Cause"
        icon={<CheckIcon />}
        items={resolutionCode ? [{ name: resolutionCode }] : []}
        truncated={truncated}
      />
    </div>
  )
}

// One column of the map: a colored header (icon + label) over a vertically-centered
// stack of tiles. Symptoms/problems past MAX_TILES collapse behind a "+N more" tile.
// Each tile carries data-tile so the funnel can find its center; the tile stack carries
// data-col so it can be used as a convergence target.
function MapColumn({ variant, label, icon, items, expanded, onExpand, stopPropagation, truncated }) {
  const overflowing = items.length > MAX_TILES && !expanded
  const shown = overflowing ? items.slice(0, MAX_TILES) : items
  const remaining = items.length - MAX_TILES

  return (
    <div className={styles.column}>
      <div className={`${styles.colHeader} ${styles[variant]}`}>
        <span className={styles.colIcon}>{icon}</span>
        <span className={styles.colLabel}>{label}</span>
      </div>
      <div className={styles.tiles} data-col={variant}>
        {shown.length === 0 && <span className={`${styles.emptyTile} ${styles[variant]}`}>NOT AVAILABLE</span>}
        {shown.map((it, i) => {
          // Hover rule: when the tile text is clipped (ellipsised) the tooltip leads with
          // the full tile text — plus the description when there is one ("name -
          // description"); when the text fits, the tooltip is just the description (and
          // nothing at all for a fully-visible Root Cause, which has none). Truncation is
          // measured post-layout in the effect above, keyed by `${variant}-${i}`.
          const desc = it.evidence || it.description
          const clipped = truncated?.has(`${variant}-${i}`)
          const hover = clipped ? (desc ? `${it.name} - ${desc}` : it.name) : desc
          return (
            <span
              key={i}
              data-tile=""
              data-tilekey={`${variant}-${i}`}
              className={`${styles.tile} ${styles[`tile_${variant}`]}`}
              title={hover || undefined}
            >
              {it.name}
            </span>
          )
        })}
        {overflowing && (
          <button
            type="button"
            data-tile=""
            className={`${styles.tile} ${styles[`tile_${variant}`]} ${styles.moreTile}`}
            onClick={e => { if (stopPropagation) e.stopPropagation(); onExpand() }}
          >
            +{remaining} more…
          </button>
        )}
      </div>
    </div>
  )
}

// Spacer between the map columns. On wide layouts it reserves the funnel's horizontal
// room and shows nothing; when the map stacks (narrow) the SVG overlay is hidden and
// this chevron marks the top-to-bottom progression instead.
function GapChevron() {
  return (
    <div className={styles.gap} aria-hidden="true">
      <span className={styles.gapChevron}>
        <Icon name="chevron-right" />
      </span>
    </div>
  )
}

function WarningIcon() {
  return <Icon name="warning" />
}

function SearchIcon() {
  return <Icon name="search" />
}

function CheckIcon() {
  return <Icon name="check-square" />
}
